import { DialogM } from "@common/components/alert/dialog/Dialog"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { congratsTypes } from "@common/screens/CongratsScreen"
import { theme } from "@common/theme/theme"
import {
    subjectPriorities,
    countryOptions,
} from "@data/constants/formConstants"
import {
    postcodeValidator,
    postcodeValidatorExistsForCountry,
} from "postcode-validator"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { putCongrats } from "@data/redux/actions/navigation"
import { authService } from "@data/services/authService"
import { backOfficeService } from "@data/services/backOfficeService"
import { hasRole } from "@data/utility/ability"
import { AbilityContext } from "@data/utility/Can"
import { findItemInListWithId } from "@data/utility/dataUtils"
import yup, { phoneRegExp } from "@data/utility/validation"
import { yupResolver } from "@hookform/resolvers/yup"
import React, { useContext, useEffect } from "react"
import { useForm } from "react-hook-form"
import { BackHandler, Dimensions, Keyboard, View } from "react-native"
import { ActivityIndicator } from "react-native-paper"
import { HeaderContainerM } from "@common/header/HeaderContainerM"
import IconButtonM from "@common/components/button/IconButtonM"
import TitleM from "@common/components/text/TitleM"
import { Tabs, TabScreen, TabsProvider } from "react-native-paper-tabs"
import { connect } from "react-redux"
import MyAccountConfigurationScreen from "./SettingsConfigurationScreen"
import MyAccountGeneralScreen from "./SettingsGeneralScreen"
import MyAccountProfileScreen from "./SettingsProfileScreen"
import MyAccountAddressScreen from "./SettingsAddressScreen"
import { toLegacyStudentAccountBody } from "./studentAccountBody"

export const HeaderM = (props) => {
    const { goBack, buttonIcon, request, onPress, title } = props

    return (
        <HeaderContainerM
            style={{ justifyContent: "space-between", width: "100%" }}
        >
            <View style={{ flexDirection: "row", alignItems: "center" }}>
                <IconButtonM
                    icon="arrow-left"
                    iconColor={theme.colors.primary}
                    size={32}
                    onPress={goBack}
                />
                <TitleM>{title}</TitleM>
            </View>
            <View style={{ marginRight: 10 }}>
                {request ? (
                    <ActivityIndicator
                        animating={true}
                        color={theme.colors.primary}
                        size={25}
                    />
                ) : (
                    <IconButtonM
                        icon={buttonIcon}
                        iconColor={theme.colors.primary}
                        size={24}
                        onPress={onPress}
                    />
                )}
            </View>
        </HeaderContainerM>
    )
}

const SettingsTabNavigator = (props) => {
    const { navigation, route } = props

    const userId = route.params?.userId

    const user = userId ? findItemInListWithId(userId, props.users) : props.user

    if (!user) {
        return (
            <ActivityIndicator animating={true} color={theme.colors.primary} />
        )
    }

    //TODO : replace with a show user in case it is another user
    //TODO : optimiser le code des formulaires en fonction du rôle

    const formSchema = yup.object().shape({
        firstname: yup.string().required(),
        lastname: yup.string().required(),
        mobile: yup
            .string()
            .nullable()
            .matches(phoneRegExp, "Le numéro de téléphone est invalide"),
        email: yup.string().email().required(),
        parent_firstname: hasRole(user, "student")
            ? yup.string().nullable()
            : null,
        parent_lastname: hasRole(user, "student")
            ? yup.string().nullable()
            : null,
        parent_email: hasRole(user, "student")
            ? yup.string().email().nullable()
            : null,
        parent_mobile: hasRole(user, "student")
            ? yup.string().required()
            : null,
        postal_code: yup
            .string()
            .nullable()
            .when("country", (country, schema) => {
                const code = country?.value
                if (code && postcodeValidatorExistsForCountry(code)) {
                    return schema.test(
                        "postcode",
                        "Le code postal est invalide",
                        (value) => !value || postcodeValidator(value, code)
                    )
                }
                return schema
            }),
    })

    const { initialTab = 0 } = route.params
    const resolver = yupResolver(formSchema)
    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors, isDirty, dirtyFields },
    } = useForm({
        resolver,
        defaultValues: {
            firstname: user?.firstname,
            lastname: user?.lastname,
            email: user?.email,
            mobile: user?.mobile,
            parent_firstname: user?.parent_firstname ?? "",
            parent_lastname: user?.parent_lastname ?? "",
            parent_email: user?.parent_email,
            parent_mobile: user?.parent_mobile,
            school_name: user?.profile?.school_name,
            city: user?.address?.city ?? "",
            street: user?.address?.street ?? "",
            complement: user?.address?.complement ?? "",
            postal_code: user?.address?.postal_code ?? "",
            country: user?.address?.country
                ? (countryOptions.find(
                      (c) => c.value === user.address.country
                  ) ?? { value: "FR", label: "France" })
                : { value: "FR", label: "France" },
            specialty_school_subjects:
                user?.specialty_school_subjects?.map((x) => {
                    return { value: x?.id.toString(), label: x?.name }
                }) ?? [],
            class:
                {
                    value: user?.profile?.class,
                    label: user?.profile?.class,
                } ?? {},
            reason:
                {
                    value: user?.reason,
                    label: user?.reason,
                } ?? {},
            future_orientation: user?.profile?.future_orientation ?? "",
            future_job: user?.profile?.future_job ?? "",
            personal_work_frequency:
                {
                    value: user?.profile?.personal_work?.frequency,
                    label: user?.profile?.personal_work?.frequency,
                } ?? {},
            personal_work_duration:
                {
                    value: user?.profile?.personal_work?.duration,
                    label: user?.profile?.personal_work?.duration,
                } ?? {},
            prioritized_school_subjects:
                user?.prioritized_school_subjects?.map((x) => {
                    return {
                        value: x?.id.toString(),
                        label: x?.name,
                        icon: x?.icon,
                    }
                }) ?? [],
            appreciated_school_subjects:
                user?.appreciated_school_subjects?.map((x) => {
                    return {
                        value: x?.id.toString(),
                        label: x?.name,
                        icon: x?.icon,
                    }
                }) ?? [],
            subject_priority:
                subjectPriorities?.find(
                    (x) => x?.value === user?.profile?.subject_priority
                ) ?? {},
            qualities:
                user?.profile?.qualities?.map((x) => {
                    return { value: x, label: x }
                }) ?? [],

            professional_situation:
                {
                    value: user?.profile?.professional_situation,
                    label: user?.profile?.professional_situation,
                } ?? {},
            study_field: user?.profile?.study_field,
            current_position: user?.profile?.current_position,
            motto: user?.profile?.motto ?? "",
            notification_settings: user?.notification_settings,
            interests: user?.profile?.interests
                ? {
                      value: user?.profile?.interests,
                      label: user?.profile?.interests,
                  }
                : null,
        },
    })

    const ability = useContext(AbilityContext)

    const onSubmit = (data) => {
        const dataStudent = toLegacyStudentAccountBody(data, dirtyFields)
        const dataTutor = {
            firstname: data.firstname,
            lastname: data.lastname,
            email: data.email,
            mobile: data.mobile,
            city: data.city,
            street: data.street,
            complement: data.complement,
            postal_code: data.postal_code,
            country: data.country?.value ?? "FR",
            professional_situation: data.professional_situation?.value,
            study_field: data.study_field,
            current_position: data.current_position,
            motto: data.motto,
            notification_settings: data.notification_settings,
        }

        if (!userId) {
            hasRole(user, "student")
                ? (props.updateStudentAccountRequest,
                  props.apiRequest(
                      authService.updateStudentAccount,
                      {},
                      dataStudent,
                      {}
                  ),
                  Keyboard.dismiss())
                : ability.can("read", "my_tutor_profile_completion")
                  ? (props.updateTutorAccountRequest,
                    props.apiRequest(
                        authService.updateTutorAccount,
                        {},
                        dataTutor,
                        {}
                    ),
                    Keyboard.dismiss())
                  : null
        } else {
            hasRole(user, "student")
                ? (props.putStudentAccountRequest,
                  props.apiRequest(
                      backOfficeService.putStudentAccount,
                      { user_id: userId },
                      dataStudent,
                      {}
                  ),
                  Keyboard.dismiss())
                : hasRole(user, "tutor") || hasRole(user, "tutor-candidate")
                  ? (props.putTutorAccountRequest,
                    props.apiRequest(
                        backOfficeService.putTutorAccount,
                        { user_id: userId },
                        dataTutor,
                        {}
                    ),
                    Keyboard.dismiss())
                  : null
        }
    }

    useApiStatus(
        backOfficeService.getUserBackoffice,
        null,
        true,
        () => {},
        () => {
            navigation.goBack()
        }
    )

    useApiStatus(
        userId
            ? backOfficeService.putStudentAccount
            : authService.updateStudentAccount,
        null,
        true,
        () => {
            const congrats = [
                {
                    congratsType: congratsTypes.GENERIC,
                    congratsData: {
                        title: "Effectué",
                        text: "Tes paramètres ont bien été mis à jour",
                    },
                },
            ]

            props.putCongrats(congrats)
            navigation.replace("congrats")
        }
    )

    useApiStatus(
        userId
            ? backOfficeService.putTutorAccount
            : authService.updateTutorAccount,
        null,
        true,
        () => {
            const congrats = [
                {
                    congratsType: congratsTypes.GENERIC,
                    congratsData: {
                        title: "Effectué",
                        text: "Tes paramètres ont bien été mis à jour",
                    },
                },
            ]

            props.putCongrats(congrats)
            navigation.replace("congrats")
        }
    )

    // Workaround to use the state in event listener
    const isDirtyRef = React.useRef(false)
    useEffect(() => {
        isDirtyRef.current = isDirty
    }, [isDirty])

    // ** Handle click on hardware back button
    useEffect(() => {
        if (userId) {
            props.apiRequest(backOfficeService.getUserBackoffice, {
                id: userId,
            })
        }

        const subscription = BackHandler.addEventListener(
            "hardwareBackPress",
            _goBack
        )
        return () => {
            subscription.remove()
        }
    }, [])

    const _goBack = () => {
        const goBack = () => {
            if (navigation.canGoBack()) {
                navigation.goBack()
            } else {
                navigation.replace("account")
            }
        }

        if (isDirtyRef.current) {
            DialogM.show({
                text1: "Modifications non enregistrées",
                text2: "Veux-tu vraiment quitter la page ?",
                buttonText1: "Quitter",
                onPress: () => {
                    goBack()
                },
                buttonText2: "Non, continuer",
            })
        } else {
            goBack()
        }
        return true
    }

    const updateRequest =
        props.updateStudentAccountRequest ||
        props.updateTutorAccountRequest ||
        props.putStudentAccountRequest ||
        props.putTutorAccountRequest

    const tabs = hasRole(user, "tutor-candidate")
        ? [
              {
                  name: "Général",
                  component: MyAccountGeneralScreen,
              },
              {
                  name: "Réglages",
                  component: MyAccountConfigurationScreen,
              },
          ]
        : [
              {
                  name: "Général",
                  component: MyAccountGeneralScreen,
              },
              {
                  name: "Adresse",
                  component: MyAccountAddressScreen,
              },
              {
                  name: "Profil",
                  component: MyAccountProfileScreen,
              },
              {
                  name: "Réglages",
                  component: MyAccountConfigurationScreen,
              },
          ]

    return (
        <>
            <HeaderM
                title={"Paramètres"}
                goBack={_goBack}
                buttonIcon={"save"}
                onPress={handleSubmit(onSubmit)}
                request={updateRequest || props.getUserBackofficeRequest}
            />
            <TabsProvider defaultIndex={initialTab ?? 0}>
                <Tabs
                    style={{
                        backgroundColor: theme.colors.white,
                        width: Dimensions.get("window").width,
                    }}
                    tabLabelStyle={
                        Dimensions.get("window").width < 800
                            ? { fontSize: 11 }
                            : undefined
                    }
                    disableSwipe={true}
                >
                    {tabs.map((tab1) => {
                        return (
                            <TabScreen label={tab1.name} key={tab1.name}>
                                {React.createElement(tab1.component, {
                                    control: control,
                                    errors: errors,
                                    setValue: setValue,
                                    navigation,
                                    userId: userId,
                                })}
                            </TabScreen>
                        )
                    })}
                </Tabs>
            </TabsProvider>
        </>
    )
}

const mapStateToProps = (state) => {
    return {
        updateStudentAccountRequest: state.api.updateStudentAccount?.request,
        updateStudentAccountSuccess: state.api.updateStudentAccount?.success,
        updateStudentAccountError: state.api.updateStudentAccount?.error,

        updateTutorAccountRequest: state.api.updateTutorAccount?.request,
        updateTutorAccountSuccess: state.api.updateTutorAccount?.success,
        updateTutorAccountError: state.api.updateTutorAccount?.error,

        putStudentAccountRequest: state.api.putStudentAccount?.request,
        putStudentAccountSuccess: state.api.putStudentAccount?.success,
        putStudentAccountError: state.api.putStudentAccount?.error,

        putTutorAccountRequest: state.api.putTutorAccount?.request,
        putTutorAccountSuccess: state.api.putTutorAccount?.success,
        putTutorAccountError: state.api.putTutorAccount?.error,

        getUserBackofficeRequest: state.api.getUserBackoffice?.request,

        user: state.data.currentUser?.object,
        token: state.data.session?.object?.token,
        users: state.data.users?.usersList,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    putCongrats,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(SettingsTabNavigator)
